"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const DISPLAY_IMAGES = [
	"/image/new-version/machining/cnc-milling/display/cnc-milling-2.webp",
	"/image/new-version/machining/cnc-milling/display/cnc-milling-3.webp",
	"/image/new-version/machining/cnc-milling/display/cnc-milling-5.webp",
	"/image/new-version/machining/cnc-milling/display/cnc-milling-12.webp",
	"/image/new-version/machining/cnc-milling/display/cnc-milling-13.webp",
	"/image/new-version/machining/cnc-milling/display/cnc-milling-14.webp",
];

const PartsCarousel = () => {
	const tMilling = useTranslations("cncMillingBanner");

	const title = tMilling("partsDisplayTitle");
	const images = DISPLAY_IMAGES;
	const altPrefix = "CNC Milling Part";
	const autoPlayInterval = 4000;
	const mobileScrollPercent = 50;
	const desktopScrollPercent = 25;
	const imageBgColor = "#bfbfbf";
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	const scrollPercent = isMobile ? mobileScrollPercent : desktopScrollPercent;

	const mobilePages = Math.ceil(images.length / 2);
	const desktopPages = Math.ceil(images.length / 4);
	const pageCount = isMobile ? mobilePages : desktopPages;

	useEffect(() => {
		if (currentIndex >= pageCount) {
			setCurrentIndex(0);
		}
	}, [pageCount, currentIndex]);

	const nextSlide = useCallback(() => {
		setCurrentIndex((prev) => (prev + 1) % pageCount);
	}, [pageCount]);

	useEffect(() => {
		if (autoPlayInterval <= 0 || pageCount <= 1) return;
		const timer = setInterval(nextSlide, autoPlayInterval);
		return () => clearInterval(timer);
	}, [nextSlide, autoPlayInterval, pageCount]);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h3 className="mb-6 text-lg font-bold text-black sm:mb-8 sm:text-xl md:mb-10 md:text-2xl lg:mb-12">
					{title}
				</h3>

				<div className="relative overflow-hidden">
					<div
						className="flex transition-transform duration-500 ease-in-out"
						style={{ transform: `translateX(-${currentIndex * scrollPercent}%)` }}
					>
						{images.map((src, index) => (
							<div key={index} className="w-1/2 flex-shrink-0 px-2 md:w-1/4 md:px-3">
								<div
									className="relative aspect-[4/3] w-full overflow-hidden"
									style={{ backgroundColor: imageBgColor }}
								>
									<Image
										src={src}
										alt={`${altPrefix} ${index + 1}`}
										fill
										className="object-contain"
										sizes="(max-width: 768px) 50vw, 25vw"
									/>
								</div>
							</div>
						))}
					</div>
				</div>

				{pageCount > 1 && (
					<div className="mt-6 flex items-center justify-center gap-2 md:mt-8">
						{Array.from({ length: pageCount }, (_, index) => (
							<button
								key={index}
								onClick={() => setCurrentIndex(index)}
								className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
									currentIndex === index ? "bg-[#9acd32]" : "bg-[#c8e69c]"
								}`}
								aria-label={`Go to page ${index + 1}`}
							/>
						))}
					</div>
				)}
			</div>
		</section>
	);
};

export default PartsCarousel;
